import * as React from 'react'

import ChevronDownIcon from '@/assets/icons/chevron-down.svg?react'
import Price from '@/components/Price'
import type {
  ChoiceCategoryList,
  ChoiceOptionList,
} from '@/lib/food-grubhub/foodRestaurantDetailModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import style from '@/views/FoodGrubhubRestaurantDetail/components/MenuItemDetailDialog/MenuItemDetailDialog.module.scss'

interface Props {
  errorIds: string[]
  customization: ChoiceCategoryList
  level?: number
  selectionMap?: Record<string, number>
  setSelectionMap?: React.Dispatch<React.SetStateAction<Record<string, number>>>
}

export default function CustomizeUI({
  errorIds,
  customization,
  selectionMap,
  setSelectionMap,
  level,
}: Props) {
  const [isShowOptional, setIsShowOptional] = useState(false)

  // const type =
  //   customization.quantity_settings?.minimum_units === 1 ||
  //   (customization.quantity_settings?.minimum_units === undefined &&
  //     customization.min_choice_options === 1)
  //     ? 'radio'
  //     : 'checkbox'

  const minUnits = customization.quantity_settings?.minimum_units
  const maxUnits = customization.quantity_settings?.maximum_units

  const type =
    (minUnits === 1 && (maxUnits ?? customization.max_choice_options) === 1) ||
    (minUnits === undefined &&
      customization.min_choice_options === 1 &&
      customization.max_choice_options === 1)
      ? 'radio'
      : 'checkbox'

  const isRequired =
    (customization.quantity_settings?.minimum_units ?? 0) > 0 ||
    (customization.min_choice_options ?? 0) > 0

  // const expandOptionalSelection = (customization.min_choice_options ?? 0) > 0 || isShowOptional
  const expandOptionalSelection = isRequired || isShowOptional

  const selectionHelperText = () => {
    if (
      (customization.quantity_settings?.minimum_units ?? 0) > 0 ||
      (customization.min_choice_options ?? 0) > 0
    ) {
      if ((customization.max_choice_options ?? 0) > (customization.min_choice_options ?? 0)) {
        return `Choose from ${customization.min_choice_options} to ${customization.max_choice_options} item${(customization.max_choice_options ?? 0) > 1 ? `s` : ``}${(customization.quantity_settings?.minimum_units ?? 0) > 1 ? `, with a total quantity of exactly ${customization.quantity_settings?.minimum_units}.` : `.`}`
      }
      if (
        (customization.max_choice_options ?? 0) === 0 &&
        (customization.min_choice_options ?? 0) > 0
      ) {
        return `Choose at least ${customization.min_choice_options} item${(customization.min_choice_options ?? 0) > 1 ? `s` : ``}${(customization.quantity_settings?.minimum_units ?? 0) > 1 ? `, with a total quantity of exactly ${customization.quantity_settings?.minimum_units}.` : `.`}`
      }
      return `Choose ${customization.min_choice_options} item${(customization.max_choice_options ?? 0) > 1 ? `s` : ``}${(customization.quantity_settings?.minimum_units ?? 0) > 1 ? `, with a total quantity of exactly ${customization.quantity_settings?.minimum_units}.` : `.`}`
    } else if (
      customization.quantity_settings?.maximum_units === null ||
      customization.max_choice_options === undefined ||
      (customization.quantity_settings?.maximum_units ?? 0) > 50
    ) {
      return `Choose as many as you'd like`
    }
    return `Choose up to ${customization.max_choice_options} item${(customization.max_choice_options ?? 0) > 1 ? `s` : ``}.`
  }

  const clearNestedOptions = (
    options: ChoiceOptionList[] | undefined,
    map: Record<string, number>,
  ) => {
    if (!options) return

    options.forEach((opt: ChoiceOptionList) => {
      if (opt.id) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- ignore
        delete map[opt.id]
      }
      if (opt.choice_category_list) {
        opt.choice_category_list.forEach((cust: ChoiceCategoryList) => {
          clearNestedOptions(cust.choice_option_list, map)
        })
      }
    })
  }

  const toggleOption = (optionId: string, checked: boolean) => {
    if (!setSelectionMap) return
    setSelectionMap(prev => {
      let newMap = {...prev}

      if (type === 'radio') {
        clearNestedOptions(customization.choice_option_list, newMap)

        if (checked) {
          newMap[optionId] = 1
        } else {
          const {[optionId]: _, ...rest} = newMap
          newMap = rest
        }
      } else if (checked) {
        newMap[optionId] = 1
      } else {
        const selectedOpt = customization.choice_option_list?.find(opt => opt.id === optionId)

        const {[optionId]: _, ...rest} = newMap
        newMap = rest

        selectedOpt?.choice_category_list?.forEach(cust => {
          clearNestedOptions(cust.choice_option_list, newMap)
        })
      }

      return newMap
    })
  }

  // const updateQty = (optionId: string, delta: number) => {
  //   if (!setSelectionMap) return
  //   setSelectionMap(prev => {
  //     const current = prev[optionId] ?? 0
  //     const newVal = current + delta

  //     if (newVal <= 0) {
  //       const {[optionId]: _, ...rest} = prev
  //       return rest
  //     }

  //     return {...prev, [optionId]: newVal}
  //   })
  // }

  const updateQty = (optionId: string, delta: number) => {
    if (!setSelectionMap) return
    setSelectionMap(prev => {
      const currentQty = prev[optionId] ?? 0
      const newQty = currentQty + delta

      if (newQty <= 0) {
        const {[optionId]: _, ...rest} = prev
        return rest
      }

      const option = customization.choice_option_list?.find(opt => opt.id === optionId)
      const optionMax = option?.quantity_settings?.maximum_units ?? Infinity
      const categoryMax = customization.quantity_settings?.maximum_units ?? Infinity

      const totalQty =
        customization.choice_option_list?.reduce(
          (sum, opt) => sum + (prev[opt.id ?? ''] ?? 0),
          0,
        ) ?? 0

      if (delta > 0) {
        if (newQty > optionMax || totalQty >= categoryMax) {
          return prev
        }
      }

      return {...prev, [optionId]: newQty}
    })
  }

  const isOptionSelected = (optionId: string, selectionMap: Record<string, number>) => {
    return Object.hasOwn(selectionMap, optionId) && (selectionMap[optionId] ?? 0) > 0
  }

  return (
    <div
      className={clsx(
        style.CategoryItem,
        errorIds.includes(customization.id ?? '') && style.HighLightedItem,
      )}
    >
      <div className={style.CategoryItemHeader}>
        <div className={style.CategoryItemTitleContainer}>
          <h1 className={style.CategoryItemTitle}>{customization.name}</h1>
          {/* <h1 className={style.CategoryItemTitle}>
            {customization.name}-choice:{customization.min_choice_options}/
            {customization.max_choice_options}-quantity:
            {customization.quantity_settings?.minimum_units}/
            {customization.quantity_settings?.maximum_units}
          </h1> */}
          <div className={style.CategoryItemDescription}>
            <VisuallyHidden>
              Maximum quantity for {customization.name} is:{' '}
              {customization.quantity_settings?.maximum_units}
            </VisuallyHidden>

            {selectionHelperText()}
          </div>
        </div>
        <div className={clsx(style.CategoryItemStatus, isRequired && style.Required)}>
          <VisuallyHidden>This option is:</VisuallyHidden>
          {isRequired ? (
            <div className={style.CategoryItemStatusContent}>Required</div>
          ) : (
            <div className={style.CategoryItemStatusContent}>
              Optional
              <button
                type='button'
                className={clsx(
                  style.CategoryItemOptionalBtn,
                  isShowOptional ? style.Expanded : style.Collapsed,
                )}
                onClick={() => {
                  setIsShowOptional(!isShowOptional)
                }}
                aria-expanded={isShowOptional}
                aria-controls={`customization-${customization.name}`}
                aria-label={
                  isShowOptional
                    ? `Collapse option ${customization.name}`
                    : `Expand option ${customization.name}`
                }
              >
                <ChevronDownIcon
                  className={clsx(
                    style.OptionalBtnIcon,
                    isShowOptional ? style.Expanded : style.Collapsed,
                  )}
                />
              </button>
            </div>
          )}
        </div>
      </div>
      {expandOptionalSelection
        ? customization.choice_option_list?.map(option => {
            const selected = !!selectionMap?.[option.id ?? '']
            const qty = selectionMap?.[option.id ?? ''] ?? 0
            const totalQty =
              customization.choice_option_list?.reduce(
                (acc, opt) => acc + (selectionMap?.[opt.id ?? ''] ?? 0),
                0,
              ) ?? 0

            const optionMax = option.quantity_settings?.maximum_units ?? Infinity
            const optionMaxLabel =
              Number.isFinite(optionMax) && optionMax > 1 ? ` (Limit: ${optionMax})` : ''
            const categoryMax = customization.quantity_settings?.maximum_units ?? Infinity

            const disableAdd = qty >= optionMax || totalQty >= categoryMax

            const disableMinus = !selected || qty <= 1

            const selectedCount =
              customization.choice_option_list?.filter(opt => {
                const qty = selectionMap?.[opt.id ?? ''] ?? 0
                return qty > 0
              }).length ?? 0

            const maxUnits = customization.quantity_settings?.maximum_units ?? Infinity
            const maxChoices = customization.max_choice_options ?? Infinity

            const disableInput = !selected && (totalQty >= maxUnits || selectedCount >= maxChoices)

            return (
              <div className={style.Detail} key={option.id}>
                <div className={style.DetailItem}>
                  <label
                    className={style.DetailItemHeader}
                    aria-label={`Option name: ${customization.name}. ${option.description}. Option price: ${(option.price?.amount ?? 0) / 100} dollar. Maximum allowed: ${optionMaxLabel}`}
                  >
                    <input
                      type={type}
                      name={`${customization.id}-${customization.name}`}
                      checked={selected}
                      disabled={type === 'checkbox' && disableInput}
                      className={clsx(
                        type === 'radio' ? style.DetailItemRadio : style.DetailItemCheckbox,
                        disableInput && style.Disabled,
                      )}
                      onChange={e => {
                        toggleOption(option.id ?? '', e.target.checked)
                      }}
                    />
                    <div className={style.DetailItemTitle}>
                      {option.description} {optionMaxLabel}
                    </div>
                  </label>
                  <div className={style.DetailItemPriceContainer}>
                    {option.choice_category_list?.length === 0 && (
                      <>
                        {selected && (option.quantity_settings?.maximum_units ?? 0) > 1 && (
                          <div className={style.DetailItemQuantity}>
                            <button
                              type='button'
                              onClick={() => {
                                updateQty(option.id ?? '', -1)
                              }}
                              disabled={disableMinus}
                              className={clsx(
                                style.DetailItemQuantityButton,
                                disableMinus && style.Disabled,
                              )}
                            >
                              -
                            </button>
                            <span className={style.DetailItemQuantityValue}>{qty}</span>
                            <button
                              type='button'
                              onClick={() => {
                                updateQty(option.id ?? '', 1)
                              }}
                              disabled={disableAdd}
                              className={clsx(
                                style.DetailItemQuantityButton,
                                disableAdd && style.Disabled,
                              )}
                            >
                              +
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    <div />
                    <div className={style.DetailItemPrice}>
                      <Price price={(option.price?.amount ?? 0) / 100} />
                    </div>
                  </div>
                </div>
                {option.choice_category_list &&
                  option.choice_category_list.length > 0 &&
                  isOptionSelected(option.id ?? '', selectionMap ?? {}) && (
                    <>
                      {option.choice_category_list.map(childCustomization => (
                        <CustomizeUI
                          key={childCustomization.id}
                          errorIds={errorIds}
                          customization={childCustomization}
                          level={(level ?? 0) + 1}
                          selectionMap={selectionMap}
                          setSelectionMap={setSelectionMap}
                        />
                      ))}
                    </>
                  )}
              </div>
            )
          })
        : null}
    </div>
  )
}
